/**
 * `/…/version-history` — one durable, crawlable page per version family.
 *
 * The document page already carries the family's single-hop links
 * (VersionRelations) and the whole graph as a client-only drawing
 * (DocumentTabs -> TreeGraph). Neither is the thing people actually search
 * for: "what replaced DOE O 420.1B", "420.1B vs 420.1C" want the WHOLE
 * chronology on one URL, in order, as text. DOE's own site removes superseded
 * revisions, so that page does not exist anywhere else — which is exactly why
 * it is worth having here.
 *
 * Server-rendered, and rendered from data the SERVER fetched: the route is a
 * real react-router route (registered through `config.addonRoutes`), so the
 * classic `asyncConnect([...])` contract applies and `loadOnServer` resolves
 * the timeline before the HTML is produced. No `useEffect` fetch — a crawler
 * that never runs the bundle still gets every link. This is the same defect
 * class the Phase-1 auto-backlinks panel had and is fixed the same way.
 *
 * READ-ONLY: the timeline is computed at request time from relations that
 * already exist (backend `@family-timeline`). Nothing is stored, so unlike the
 * derived full-text/glossary fields there is no reconciler and nothing that
 * can go stale.
 *
 * Canonical: every member's hub URL serves the identical list, so the page
 * declares the backend's `canonical_url` member as the canonical one. Only
 * that URL is submitted in the sitemap (express/sitemapBrowse.js). A family
 * that has collapsed to a single visible member gets `noindex` rather than a
 * page whose only entry is the document you came from.
 */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { UniversalLink } from '@plone/volto/components';
import { asyncConnect } from '@plone/volto/helpers/AsyncConnect';
import { Helmet, flattenToAppURL } from '@plone/volto/helpers';

import { getFamilyTimeline } from '../../actions/familyTimeline';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import { PUBLISHER_NAME } from '../../siteConfig/branding';
import { documentPathOf, timelineUrlOf } from './url';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** ISO date -> "Jan 04, 2017". Parsed, not `new Date()`: a Date would apply
 *  the process timezone and could render a different day on the server than in
 *  the browser, which React would then flag as a hydration mismatch. */
const fmtDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[3]}, ${m[1]}` : null;
};

/** How entry N+1 relates to entry N, said from entry N's point of view. */
const CONNECTOR = {
  supersedes: 'superseded by',
  cancels: 'cancelled by',
};

const Entry = ({ item, position }) => {
  const href = flattenToAppURL(item.url || '');
  const date = fmtDate(item.date);
  return (
    <li
      className={`pp-family__item${item.is_current ? ' pp-family__item--current' : ''}`}
      aria-current={item.is_context ? 'page' : undefined}
    >
      <div className="pp-family__marker" aria-hidden="true">
        {position}
      </div>
      <div className="pp-family__body">
        <h2 className="pp-family__doc">
          <UniversalLink href={href} className="pp-family__link">
            {item.docid ? (
              <span className="pp-family__docid">{item.docid}</span>
            ) : null}
            <span className="pp-family__title">{item.title}</span>
          </UniversalLink>
        </h2>
        <p className="pp-family__meta">
          {item.is_current ? (
            <span className="pp-family__pill">Current version</span>
          ) : null}
          {item.status ? (
            <span className="pp-family__status">{item.status}</span>
          ) : null}
          {date ? <span className="pp-family__date">Issued {date}</span> : null}
          {item.superseded_date ? (
            <span className="pp-family__date">
              Retired {fmtDate(item.superseded_date)}
            </span>
          ) : null}
        </p>
      </div>
    </li>
  );
};

export const FamilyTimeline = ({ timeline, documentPath, loading }) => {
  // The raw backend `@id` still carries the site segment, so it resolves the
  // brand even on a route that never loads a content object into the store.
  const site = useSiteConfig({ '@id': timeline?.context_url });
  const origin = (site.baseUrl || '').replace(/\/+$/, '');
  const items = timeline?.items || [];
  const count = timeline?.count ?? items.length;

  const canonicalPath = timeline?.canonical_url
    ? timelineUrlOf(flattenToAppURL(timeline.canonical_url))
    : timelineUrlOf(documentPath);
  const canonical = origin ? `${origin}${canonicalPath}` : canonicalPath;

  const current = items.find((it) => it.is_current) || items[items.length - 1];
  const context = items.find((it) => it.is_context);
  const label = current?.docid || current?.title || '';
  const heading = label ? `Version history of ${label}` : 'Version history';
  const description = label
    ? `Every revision of ${label} held in ${site.title}, oldest first — ` +
      `${count} document${count === 1 ? '' : 's'}, with the supersession and ` +
      `cancellation dates that connect them.`
    : `Version history in ${site.title}.`;

  // A family is only a family once it has two visible members. One member
  // means the document has no supersession relations (or its only relatives
  // are private): a page listing the document you came from is thin content,
  // so it renders as a pointer back and asks not to be indexed.
  const thin = !loading && count <= 1;

  // Topological order does not imply every neighbouring pair is joined by a
  // direct edge. A family is not always a chain: a revision routinely replaces
  // SEVERAL earlier documents at once, which pulls each of their own histories
  // into this one (DOE O 420.1C's family is 161 documents for exactly that
  // reason [measured 2026-08-05]). Say so rather than implying a single line
  // of descent.
  const linked = items.filter((it) => it.relation_to_next).length;
  const branched = items.length > 1 && linked < items.length - 1;

  // See PrivacyNotice: an addonRoutes page renders its own root, so it has to
  // carry the skiplink's #view target itself (WCAG 2.4.1).
  return (
    <div className="pp-family" id="view">
      <Helmet>
        <title>{`${heading} | ${site.seoName}`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {thin ? <meta name="robots" content="noindex, follow" /> : null}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={heading} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content={PUBLISHER_NAME} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <div className="prc-kit__main">
        <article className="prc-kit__doc">
          <h1 className="prc-kit__doc-h1">{heading}</h1>
          <p className="pp-family__lede">{description}</p>

          {branched ? (
            <p className="pp-family__note">
              This history is not a single line of descent. Some of these
              documents are here because one revision replaced several earlier
              documents at once, bringing their own histories with it.
            </p>
          ) : null}

          {items.length > 0 ? (
            <ol className="pp-family__list">
              {items.map((item, index) => (
                <React.Fragment key={item.url || index}>
                  <Entry item={item} position={index + 1} />
                  {item.relation_to_next ? (
                    <li className="pp-family__link-row">
                      <span className="pp-family__connector">
                        {CONNECTOR[item.relation_to_next] ||
                          item.relation_to_next}
                      </span>
                    </li>
                  ) : null}
                </React.Fragment>
              ))}
            </ol>
          ) : (
            <p className="pp-family__empty">
              {loading
                ? 'Loading version history…'
                : 'No version history is recorded for this document.'}
            </p>
          )}

          {context || documentPath ? (
            <p className="pp-family__back">
              <UniversalLink
                href={context ? flattenToAppURL(context.url) : documentPath}
              >
                Back to {context?.docid || context?.title || 'the document'}
              </UniversalLink>
            </p>
          ) : null}
        </article>
      </div>
    </div>
  );
};

export default compose(
  asyncConnect([
    {
      key: 'familyTimeline',
      promise: ({ location, store: { dispatch } }) =>
        dispatch(getFamilyTimeline(documentPathOf(location.pathname))),
    },
  ]),
  connect((state, props) => ({
    timeline: state.familyTimeline?.data,
    loading: state.familyTimeline?.loading,
    documentPath: documentPathOf(props.location?.pathname || ''),
  })),
)(FamilyTimeline);
